import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSelector } from 'react-redux';
import RoleShell from '../../components/Enrollment/RoleShell';
import { Prod01 } from '../../constants/ActionType';
import { useSortableTable, SortableTh, nameFromString } from '../../components/SortableTable';
import { formatCalendarDate, formatDate, formatDateTime } from '../../utils/dateFormat';

const NURSE_BONUS_SORT_COLUMNS = {
    name: { type: 'name', value: (r) => nameFromString(r?.name) },
    patients: { type: 'number', value: (r) => r?.patients },
    minutes: { type: 'number', value: (r) => r?.minutes },
    credits: { type: 'number', value: (r) => r?.credits },
    billable: { type: 'number', value: (r) => r?.billableDollars },
    nonBillable: { type: 'number', value: (r) => r?.nonBillableMinutes },
    effort: { type: 'number', value: (r) => r?.effortDollars },
    total: { type: 'number', value: (r) => r?.total },
};
const DUPLICATE_SORT_COLUMNS = {
    when: { type: 'date', value: (r) => r?.loggedAt },
    program: { type: 'string', value: (r) => r?.program },
    nurse: { type: 'name', value: (r) => nameFromString(r?.loggedByName) },
    minutes: { type: 'number', value: (r) => r?.minutes },
    rule: { type: 'string', value: (r) => r?.duplicate?.rule },
};
const GATE_SORT_COLUMNS = {
    when: { type: 'date', value: (r) => r?.at },
    patient: { type: 'name', value: (r) => nameFromString(r?.patientName) },
    program: { type: 'string', value: (r) => r?.program },
    nurse: { type: 'name', value: (r) => nameFromString(r?.loggedByName) },
    minutes: { type: 'number', value: (r) => r?.minutes },
};
const PAYER_SORT_COLUMNS = {
    name: { type: 'string', value: (p) => p?.name },
    type: { type: 'string', value: (p) => p?.type },
    plan: { type: 'string', value: (p) => p?.plan },
    followsMedicare: { type: 'boolean', value: (p) => p?.followsMedicare },
};

const err = (e) => e?.response?.data?.message || e.message;
const money = (n, d = 2) => `$${Number(n || 0).toFixed(d)}`;
const MANAGERS = ['admin', 'adminStaff'];
const PAYER_EDITORS = ['admin', 'billing'];

const MonthPicker = ({ year, month, onChange }) => (
    <span style={{ fontSize: 13 }}>
        <button className="btn btn-sm btn-light" onClick={() => { const m = moment({ year, month: month - 1 }).subtract(1, 'month'); onChange(m.year(), m.month() + 1); }}>&lsaquo;</button>
        <b className="mx-2">{moment({ year, month: month - 1 }).format('MMMM YYYY')}</b>
        <button className="btn btn-sm btn-light" onClick={() => { const m = moment({ year, month: month - 1 }).add(1, 'month'); onChange(m.year(), m.month() + 1); }}>&rsaquo;</button>
    </span>
);

// Monthly nurse bonus credits report: Org Manager (own org), Super Admin, Accounting; nurses see only their own line.
export const NurseBonusReport = () => {
    const [year, setYear] = useState(moment().year());
    const [month, setMonth] = useState(moment().month() + 1);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        setError(null);
        try { setData((await axios.get(`${Prod01}/billing/nurse-bonus?year=${year}&month=${month}`)).data); } catch (e) { setError(err(e)); }
    }, [year, month]);
    useEffect(() => { load(); }, [load]);
    const tableSort = useSortableTable(data?.rows, { id: 'billing.nurseBonus', columns: NURSE_BONUS_SORT_COLUMNS, defaultSort: { key: 'name', dir: 'asc' } });
    return (
        <RoleShell title="Nurse bonus credits">
            <div className="shadow-lg p-3 bg-white rounded">
                <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                    <h5 className="mb-0">Nurse bonus credits</h5>
                    <span className="ms-auto"><MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} /></span>
                </div>
                {error ? <div className="alert alert-danger py-1">{error}</div> : null}
                {data ? (
                    <>
                        <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                            {money(data.rates.creditDollar)} per credit &middot; {money(data.rates.effortPerMinute, 4)} per non-billable minute &middot; {data.note}
                        </div>
                        <table className="table table-sm" style={{ fontSize: 13 }}>
                            <thead><tr><SortableTh sortKey="name" sort={tableSort}>Nurse</SortableTh><SortableTh sortKey="patients" sort={tableSort}>Patients</SortableTh>
                                <SortableTh sortKey="minutes" sort={tableSort}>Minutes</SortableTh><SortableTh sortKey="credits" sort={tableSort}>Credits</SortableTh>
                                <SortableTh sortKey="billable" sort={tableSort}>Billable $</SortableTh><SortableTh sortKey="nonBillable" sort={tableSort}>Non-billable min</SortableTh>
                                <SortableTh sortKey="effort" sort={tableSort}>Effort $</SortableTh><SortableTh sortKey="total" sort={tableSort}>Total</SortableTh></tr></thead>
                            <tbody>
                                {data.rows.length ? tableSort.rows.map((r) => (
                                    <tr key={r.nurseId}>
                                        <td>{r.name || r.nurseId}{r.pendingDuplicateMinutes ? <span className="badge bg-warning text-dark ms-1" title="Includes minutes flagged as a likely duplicate, pending Org Manager review">{r.pendingDuplicateMinutes} min pending review</span> : null}</td>
                                        <td>{r.patients}</td><td>{r.minutes}</td><td>{r.credits}</td><td>{money(r.billableDollars)}</td>
                                        <td>{r.nonBillableMinutes}</td><td>{money(r.effortDollars, 4)}</td><td><b>{money(r.total, 4)}</b></td>
                                    </tr>
                                )) : <tr><td colSpan={8} className="text-muted">No time logged this month.</td></tr>}
                            </tbody>
                        </table>
                    </>
                ) : null}
            </div>
        </RoleShell>
    );
};

// Likely-duplicate review queue (Org Manager / Super Admin). Never auto-deleted; removal needs a reason and is audited.
export const DuplicateReview = () => {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState('pending');
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        setError(null);
        try { setRows((await axios.get(`${Prod01}/billing/duplicates?status=${status}`)).data); } catch (e) { setError(err(e)); }
    }, [status]);
    useEffect(() => { load(); }, [load]);
    const tableSort = useSortableTable(rows, { id: 'billing.duplicateReview', columns: DUPLICATE_SORT_COLUMNS, defaultSort: { key: 'when', dir: 'desc' } });
    const review = async (r, action) => {
        const reason = window.prompt(action === 'remove' ? 'Reason for removing this duplicate (required):' : 'Note for keeping (optional):') ?? null;
        if (reason === null || (action === 'remove' && reason.trim().length < 3)) return;
        try { await axios.post(`${Prod01}/billing/duplicates/${r.kind}/${r.id}`, { action, reason }); load(); } catch (e) { setError(err(e)); }
    };
    return (
        <RoleShell title="Duplicate time review">
            <div className="shadow-lg p-3 bg-white rounded">
                <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                    <h5 className="mb-0">Likely duplicate entries</h5>
                    <select className="form-select form-select-sm ms-auto" style={{ width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="pending">Pending review</option><option value="kept">Kept</option><option value="removed">Removed</option>
                    </select>
                </div>
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>Same patient, program and day by different nurses with a similar note (&ge; 0.8) or the same activity within 30 min. Pending entries still count toward billable time; removed entries are excluded.</div>
                {error ? <div className="alert alert-danger py-1">{error}</div> : null}
                <table className="table table-sm" style={{ fontSize: 13 }}>
                    <thead><tr><SortableTh sortKey="when" sort={tableSort}>When</SortableTh><SortableTh sortKey="program" sort={tableSort}>Program</SortableTh>
                        <SortableTh sortKey="nurse" sort={tableSort}>Nurse</SortableTh><SortableTh sortKey="minutes" sort={tableSort}>Minutes</SortableTh>
                        <th>Activity / note</th><SortableTh sortKey="rule" sort={tableSort}>Rule</SortableTh><th></th></tr></thead>
                    <tbody>
                        {rows.length ? tableSort.rows.map((r) => (
                            <tr key={`${r.kind}-${r.id}`}>
                                <td>{formatDateTime(r.loggedAt)}</td><td>{r.program}</td><td>{r.loggedByName || r.loggedById}</td><td>{r.minutes}</td>
                                <td style={{ maxWidth: 320 }}>{r.activityType ? <b>{r.activityType}: </b> : null}{String(r.note || '').slice(0, 160)}</td>
                                <td>{r.duplicate?.rule}{r.duplicate?.score != null ? ` (${r.duplicate.score})` : ''}{r.duplicate?.reason ? <div className="text-muted">{r.duplicate.reason}</div> : null}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{status === 'pending' ? (<>
                                    <button className="btn btn-sm btn-outline-success me-1" onClick={() => review(r, 'keep')}>Keep</button>
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => review(r, 'remove')}>Remove</button>
                                </>) : null}</td>
                            </tr>
                        )) : <tr><td colSpan={7} className="text-muted">Nothing to review.</td></tr>}
                    </tbody>
                </table>
            </div>
        </RoleShell>
    );
};

// Time saved without enrollment during the warn-only grace period (Org Manager / Super Admin).
export const EnrollmentWarnings = () => {
    const [data, setData] = useState(null);
    const [status, setStatus] = useState('open');
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        setError(null);
        try { setData((await axios.get(`${Prod01}/billing/enrollment-warnings?status=${status}`)).data); } catch (e) { setError(err(e)); }
    }, [status]);
    useEffect(() => { load(); }, [load]);
    const rows = data?.rows || [];
    const tableSort = useSortableTable(rows, { id: 'billing.enrollmentGate', columns: GATE_SORT_COLUMNS, defaultSort: { key: 'when', dir: 'desc' } });
    const gate = data?.gate;
    return (
        <RoleShell title="Enrollment warnings">
            <div className="shadow-lg p-3 bg-white rounded">
                <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                    <h5 className="mb-0">Time logged before enrollment</h5>
                    <select className="form-select form-select-sm ms-auto" style={{ width: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="open">Needs enrollment</option><option value="resolved">Enrolled since</option><option value="all">All</option>
                    </select>
                </div>
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                    {gate ? (gate.mode === 'warn' ? `Grace period: time saves with a warning until ${formatDate(gate.warnUntil)}, then enrollment is required.` : 'Enrollment is required before time can be logged.') : null}
                    {' '}These minutes are not billable until the patient is enrolled in the program with consent.
                </div>
                {error ? <div className="alert alert-danger py-1">{error}</div> : null}
                <table className="table table-sm" style={{ fontSize: 13 }}>
                    <thead><tr><SortableTh sortKey="when" sort={tableSort}>When</SortableTh><SortableTh sortKey="patient" sort={tableSort}>Patient</SortableTh>
                        <SortableTh sortKey="program" sort={tableSort}>Program</SortableTh><SortableTh sortKey="nurse" sort={tableSort}>Nurse</SortableTh>
                        <SortableTh sortKey="minutes" sort={tableSort}>Minutes</SortableTh><th>Status</th></tr></thead>
                    <tbody>
                        {rows.length ? tableSort.rows.map((r) => (
                            <tr key={`${r.kind}-${r.id}`}>
                                <td>{formatDate(r.at)}</td><td>{r.patientName}</td><td>{r.program}</td><td>{r.loggedByName || ''}</td><td>{r.minutes}</td>
                                <td>{r.resolved ? 'Enrolled since' : <span style={{ color: '#856404' }}>{r.message}</span>}</td>
                            </tr>
                        )) : <tr><td colSpan={6} className="text-muted">Nothing listed.</td></tr>}
                    </tbody>
                </table>
            </div>
        </RoleShell>
    );
};

// Payer table (admin/billing edit; audited) + CSV/XLSX import of the 'Payer Rules' sheet (data from row 8).
export const PayerAdmin = () => {
    const { user } = useSelector((s) => s.auth);
    const canEdit = PAYER_EDITORS.includes(user?.role);
    const [payers, setPayers] = useState([]);
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        try { setPayers((await axios.get(`${Prod01}/billing/payers?includeInactive=true`)).data); } catch (e) { setError(err(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const tableSort = useSortableTable(payers, { id: 'billing.payers', columns: PAYER_SORT_COLUMNS, defaultSort: { key: 'name', dir: 'asc' } });
    const upload = async (dryRun) => {
        if (!file) return;
        setError(null);
        const fd = new FormData();
        fd.append('file', file);
        try { setResult((await axios.post(`${Prod01}/billing/payers/import?dryRun=${dryRun}`, fd)).data); if (!dryRun) load(); } catch (e) { setError(err(e)); }
    };
    const toggle = async (p) => {
        try { await axios.post(`${Prod01}/billing/payers/${p._id}`, { active: !p.active }); load(); } catch (e) { setError(err(e)); }
    };
    return (
        <RoleShell title="Payers">
            <div className="shadow-lg p-3 bg-white rounded">
                <h5>Payers</h5>
                {error ? <div className="alert alert-danger py-1">{error}</div> : null}
                {canEdit ? (
                    <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                        <input type="file" accept=".csv,.xlsx,.xls" className="form-control form-control-sm" style={{ maxWidth: 320 }} onChange={(e) => setFile(e.target.files[0])} />
                        <button className="btn btn-sm btn-outline-secondary" disabled={!file} onClick={() => upload(true)}>Preview import</button>
                        <button className="btn btn-sm btn-primary" disabled={!file} onClick={() => upload(false)}>Import</button>
                        <small className="text-muted">Sheet &lsquo;Payer Rules&rsquo;; row 7 is the example row; data from row 8.</small>
                    </div>
                ) : null}
                {result ? (
                    <div className="alert alert-light border py-1" style={{ fontSize: 12 }}>
                        {result.dryRun ? 'Preview' : 'Imported'}: {result.total} rows, {result.created} created, {result.updated} updated, {result.errors} errors
                        {result.results.filter((r) => r.status === 'error').map((r) => <div key={r.row}>Row {r.row}: {r.errors.join('; ')}</div>)}
                    </div>
                ) : null}
                <table className="table table-sm" style={{ fontSize: 12 }}>
                    <thead><tr><SortableTh sortKey="name" sort={tableSort}>Payer</SortableTh><SortableTh sortKey="type" sort={tableSort}>Type</SortableTh>
                        <SortableTh sortKey="plan" sort={tableSort}>Plan</SortableTh><SortableTh sortKey="followsMedicare" sort={tableSort}>Follows Medicare</SortableTh><th>Not covered</th><th>Prior auth / unknown</th><th>Effective</th><th>Source</th><th></th></tr></thead>
                    <tbody>
                        {tableSort.rows.map((p) => (
                            <tr key={p._id} className={p.active ? '' : 'text-muted'}>
                                <td><b>{p.name}</b></td><td>{p.type}</td><td>{p.plan}</td><td>{p.followsMedicare ? 'Yes' : 'No'}</td>
                                <td>{(p.coverage || []).filter((c) => c.status === 'No').map((c) => c.code).join(', ')}</td>
                                <td>{(p.coverage || []).filter((c) => ['PriorAuth', 'Unknown'].includes(c.status)).map((c) => `${c.code} (${c.status})`).join(', ')}</td>
                                <td>{formatCalendarDate(p.effectiveFrom)}{p.effectiveTo ? ` - ${formatCalendarDate(p.effectiveTo)}` : ''}</td>
                                <td>{p.source}</td>
                                <td>{canEdit && !p.seedKey ? <button className="btn btn-sm btn-link p-0" onClick={() => toggle(p)}>{p.active ? 'Deactivate' : 'Activate'}</button> : null}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </RoleShell>
    );
};

// POC expiry badge: stays until a new POC is recorded (computed live by the API).
export const PocExpiryBadge = ({ style }) => {
    const [data, setData] = useState(null);
    useEffect(() => { axios.get(`${Prod01}/billing/poc-expiry`).then((r) => setData(r.data)).catch(() => setData(null)); }, []);
    if (!data || !data.count) return null;
    return <span className="badge bg-warning text-dark ms-1" style={style} title={`${data.count} plan(s) of care expiring within 30 days or expired (${data.expired || 0} expired)`}>POC {data.count}</span>;
};

export const PocExpiryList = () => {
    const [data, setData] = useState(null);
    useEffect(() => { axios.get(`${Prod01}/billing/poc-expiry`).then((r) => setData(r.data)).catch(() => setData({ rows: [] })); }, []);
    if (!data || !data.rows.length) return null;
    return (
        <div className="shadow-lg p-3 mb-4 bg-white rounded">
            <h6 className="mb-2" style={{ fontWeight: 700 }}>Plans of care due for review <span className="badge bg-warning text-dark">{data.count}</span></h6>
            <div className="text-muted mb-1" style={{ fontSize: 11 }}>A follow-up POC is current for 12 months (CMS: reviewed/revised at least annually). Alerts 30 days before and at expiry.</div>
            <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                <tbody>
                    {data.rows.slice(0, 25).map((r) => (
                        <tr key={`${r.patientId}-${r.program}`}>
                            <td>{r.patientName}</td><td>{r.program}</td><td>{formatCalendarDate(r.expiresAt)}</td>
                            <td>{r.level === 'expired' ? <span className="badge bg-danger">expired</span> : <span className="badge bg-warning text-dark">{r.daysLeft} days</span>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Shared-patient care team: Org Manager / Super Admin assign several nurses (own org, audited); minutes per nurse.
export const CareTeamPanel = ({ patientId, nurses = [] }) => {
    const { user } = useSelector((s) => s.auth);
    const [data, setData] = useState(null);
    const [sel, setSel] = useState([]);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        try { const r = await axios.get(`${Prod01}/billing/care-team/${patientId}`); setData(r.data); setSel(r.data.nurses.filter((n) => n.shared).map((n) => n.nurseId)); } catch (e) { setError(err(e)); }
    }, [patientId]);
    useEffect(() => { if (patientId) load(); }, [patientId, load]);
    const save = async () => {
        try { await axios.post(`${Prod01}/billing/care-team/${patientId}`, { nurseIds: sel }); load(); } catch (e) { setError(err(e)); }
    };
    if (!data) return error ? <div className="text-danger" style={{ fontSize: 12 }}>{error}</div> : null;
    return (
        <div className="p-2 border rounded mb-2" style={{ fontSize: 13 }}>
            <b>Care team</b>
            {error ? <div className="text-danger">{error}</div> : null}
            <div>{data.nurses.map((n) => n.name || n.nurseId).join(', ') || <span className="text-muted">no nurse assigned</span>}</div>
            {data.minutes.length ? <div className="text-muted">This month: {data.minutes.map((m) => `${m.name || 'nurse'} ${m.total} min${m.pendingDuplicate ? ` (${m.pendingDuplicate} pending review)` : ''}`).join(' · ')}</div> : null}
            {MANAGERS.includes(user?.role) && nurses.length ? (
                <div className="mt-1">
                    <select multiple className="form-select form-select-sm" value={sel} onChange={(e) => setSel([...e.target.selectedOptions].map((o) => o.value))}>
                        {nurses.map((n) => <option key={n._id} value={n._id}>{`${n.firstname || ''} ${n.lastname || ''}`.trim()}</option>)}
                    </select>
                    <button className="btn btn-sm btn-primary mt-1" onClick={save}>Save care team</button>
                </div>
            ) : null}
        </div>
    );
};
